import React from 'react';
import { Card } from '@/components/ui/card';
import { CheckCircle, XCircle, Calendar, MapPin, Hotel, Star, Clock } from 'lucide-react';

const PortalItineraryView = ({ itinerary }) => {
  if (!itinerary) return null;

  const hotels = itinerary.hotels || [];
  const days = itinerary.days || [];
  const inclusions = itinerary.inclusions || [];
  const exclusions = itinerary.exclusions || [];

  return (
    <>
      {hotels.length > 0 && (
        <Card className="p-6 sm:p-8 bg-white rounded-3xl shadow-xl" data-testid="portal-hotels">
          <h2 className="text-xl font-heading font-bold text-slate-900 mb-6 flex items-center gap-2">
            <Hotel className="h-5 w-5 text-primary-700" />Accommodation
          </h2>
          <div className="space-y-4">
            {hotels.map((hotel, idx) => (
              <PortalHotelCard key={`hotel-${hotel.hotelName || idx}`} hotel={hotel} />
            ))}
          </div>
        </Card>
      )}

      {days.length > 0 && (
        <Card className="p-6 sm:p-8 bg-white rounded-3xl shadow-xl" data-testid="portal-itinerary-days">
          <h2 className="text-xl font-heading font-bold text-slate-900 mb-6 flex items-center gap-2">
            <Calendar className="h-5 w-5 text-primary-700" />Day-wise Itinerary
          </h2>
          <div className="space-y-4">
            {days.map((day, idx) => (
              <div key={`day-${day.dayNumber || idx}`} className="flex gap-4">
                <div className="w-10 h-10 bg-primary-100 rounded-xl flex items-center justify-center flex-shrink-0">
                  <span className="text-sm font-bold text-primary-700">{day.dayNumber}</span>
                </div>
                <div className="flex-1">
                  <h3 className="font-semibold text-slate-900 text-sm">{day.title}</h3>
                  {day.description && <p className="text-sm text-slate-600 mt-1 whitespace-pre-line">{day.description}</p>}
                </div>
              </div>
            ))}
          </div>
        </Card>
      )}

      {(inclusions.length > 0 || exclusions.length > 0) && (
        <Card className="p-6 sm:p-8 bg-white rounded-3xl shadow-xl" data-testid="portal-inclexcl">
          <h2 className="text-xl font-heading font-bold text-slate-900 mb-6">Package Details</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
            {inclusions.length > 0 && (
              <div>
                <h3 className="font-semibold text-emerald-700 mb-3">Inclusions</h3>
                <ul className="space-y-2">
                  {inclusions.map((item, idx) => (
                    <li key={`inc-${idx}-${item.slice(0, 8)}`} className="text-sm text-slate-600 flex items-start gap-2">
                      <CheckCircle className="h-4 w-4 text-emerald-500 mt-0.5 flex-shrink-0" />{item}
                    </li>
                  ))}
                </ul>
              </div>
            )}
            {exclusions.length > 0 && (
              <div>
                <h3 className="font-semibold text-rose-700 mb-3">Exclusions</h3>
                <ul className="space-y-2">
                  {exclusions.map((item, idx) => (
                    <li key={`exc-${idx}-${item.slice(0, 8)}`} className="text-sm text-slate-600 flex items-start gap-2">
                      <XCircle className="h-4 w-4 text-rose-500 mt-0.5 flex-shrink-0" />{item}
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        </Card>
      )}

      {itinerary.termsAndConditions && (
        <Card className="p-6 sm:p-8 bg-white rounded-3xl shadow-xl" data-testid="portal-terms">
          <h2 className="text-xl font-heading font-bold text-slate-900 mb-4">Terms & Conditions</h2>
          <p className="text-sm text-slate-600 whitespace-pre-line">{itinerary.termsAndConditions}</p>
        </Card>
      )}

      {itinerary.paymentPolicy && (
        <Card className="p-6 sm:p-8 bg-white rounded-3xl shadow-xl" data-testid="portal-payment-policy">
          <h2 className="text-xl font-heading font-bold text-slate-900 mb-4">Payment & Cancellation Policy</h2>
          <p className="text-sm text-slate-600 whitespace-pre-line">{itinerary.paymentPolicy}</p>
        </Card>
      )}
    </>
  );
};

const PortalHotelCard = ({ hotel }) => (
  <div className="p-4 bg-slate-50 rounded-xl border border-slate-100">
    <div className="flex justify-between items-start mb-2">
      <h3 className="font-semibold text-slate-900">{hotel.hotelName}</h3>
      {hotel.starRating && (
        <span className="flex items-center gap-1 text-amber-600 text-sm">
          <Star className="h-3.5 w-3.5 fill-amber-500" />{hotel.starRating}
        </span>
      )}
    </div>
    {hotel.city && <p className="text-sm text-slate-500 flex items-center gap-1"><MapPin className="h-3 w-3" />{hotel.city}</p>}
    <div className="flex flex-wrap gap-3 mt-2 text-xs text-slate-600">
      {hotel.roomType && <span className="bg-white px-2 py-1 rounded-lg border">Room: {hotel.roomType}</span>}
      {hotel.mealPlan?.length > 0 && <span className="bg-white px-2 py-1 rounded-lg border">Meals: {hotel.mealPlan.join(', ')}</span>}
      {hotel.checkInDate && (
        <span className="bg-white px-2 py-1 rounded-lg border flex items-center gap-1">
          <Clock className="h-3 w-3" />{hotel.checkInDate} - {hotel.checkOutDate}
        </span>
      )}
    </div>
  </div>
);

export default PortalItineraryView;
